import * as CHDeviceActions from "@app/project/store/actions/devices.actions";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store, select } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { CHService } from "@app/project/model/ch-service";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { DataActions } from "@app/project/store/actions";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { ActionWord } from "../../../shared/resources/action-word";
import { CHServiceActions } from "../actions";
import { ServiceAction } from "../actions/ch-services.actions";
import { ProjectSelectors } from "../selectors";

@Injectable()
export class ServiceEffects {
    constructor(
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly actions$: Actions,
        private readonly serviceService: CHServiceService,
        private readonly auditLogService: AuditLogService,
        private readonly store: Store
    ) {}

    AUDIO_SOURCE_DEF_ID = "com.clarecontrols.widgets.audio.source";
    INTRUSION_PERIPHERAL_DEF_ID = "com.clarecontrols.widgets.intrusion.peripheral";
    SERVICE_DEF_BLACKLIST = [this.AUDIO_SOURCE_DEF_ID, this.INTRUSION_PERIPHERAL_DEF_ID];

    getLiteServices$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.GetLiteServices),
            map((action: CHServiceActions.GetLiteServices) => action),
            switchMap(action =>
                this.serviceService.getLiteServiceInfos(action.payload.uuid).pipe(
                    checkCurrentID<any[]>(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid),
                    map(services => {
                        const payload = { uuid: action.payload.uuid, result: {} };
                        if (services) {
                            payload.result = services.reduce((items, data) => {
                                if (data.definitionId !== this.AUDIO_SOURCE_DEF_ID) {
                                    items[data.ServiceInstanceId] = CHService.fromDbObject(data);
                                }
                                return items;
                            }, {});
                        }

                        return new CHServiceActions.GetLiteServicesSuccess(payload);
                    }),
                    catchError(DataActions.toError(CHServiceActions.GetLiteServicesFailure))
                )
            )
        )
    );

    getLiteServicesFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.GetLiteServicesFailure),
            map(DataActions.handleErrorMessage(ActionWord.retrieved, "devices"))
        )
    );

    getServiceToDeviceMap$: Observable<Action[] | Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.GetServiceToDeviceMap),
            map((action: CHServiceActions.GetServiceToDeviceMap) => action),
            switchMap(action =>
                this.serviceService.getServiceToDeviceMap(action.payload.uuid).pipe(
                    checkCurrentID<any>(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid),
                    mergeMap(serviceToDeviceMap => {
                        const services = serviceToDeviceMap.Services.reduce((items, data) => {
                            if (data.definitionId !== this.AUDIO_SOURCE_DEF_ID) {
                                items[data.instanceId] = CHService.fromDbObject(data);
                            }
                            return items;
                        }, {});

                        return [
                            new CHServiceActions.GetServicesSuccess({
                                uuid: action.payload.uuid,
                                result: services,
                            }),
                            new CHDeviceActions.GetDevicesSuccess({
                                uuid: action.payload.uuid,
                                result: serviceToDeviceMap.Devices,
                            }),
                        ];
                    }),
                    catchError(error =>
                        of(new CHServiceActions.GetServiceToDeviceMapFailure({ error }), new CHDeviceActions.GetDevicesFailure({ error }))
                    )
                )
            )
        )
    );

    deleteServiceMap$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.DeleteServiceMap),
            map((action: CHServiceActions.DeleteServiceMap) => action),
            map(
                action =>
                    new CHDeviceActions.DeleteDeviceConfirmation({
                        uuid: action.payload.uuid,
                        data: action.payload.device,
                        callbackEmitter: action.payload.callbackEmitter || null,
                        deviceName: action.payload.deviceName,
                        logAction: action.payload.logAction,
                    })
            ),
            catchError(DataActions.toError(CHServiceActions.DeleteServiceFailure))
        )
    );

    deleteService$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.DeleteService),
            exhaustMap((action: ServiceAction) =>
                DataActions.toAction()(
                    this.serviceService
                        .deleteService(action.payload.uuid, action.payload.data)
                        .pipe(checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid)),
                    CHServiceActions.DeleteServiceSuccess,
                    CHServiceActions.DeleteServiceFailure,
                    action.payload.callbackEmitter
                )
            ),
            catchError(DataActions.toError(CHServiceActions.DeleteServiceFailure))
        )
    );

    deleteServiceConfirmation$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.DeleteServiceConfirmation),
            exhaustMap((action: ServiceAction) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    CHServiceActions.DeleteServiceConfirmationDismiss,
                    CHServiceActions.DeleteService,
                    action.payload.data.name
                )
            )
        )
    );

    getServiceToDeviceMapFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.GetServiceToDeviceMapFailure),
            map(DataActions.handleErrorMessage(ActionWord.retrieved, "devices"))
        )
    );

    updateServiceNameAndZone$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.UpdateServiceNameAndZone),
            map((action: CHServiceActions.UpdateServiceNameAndZone) => action),
            switchMap(action =>
                this.serviceService
                    .updateServiceNameAndZone(action.payload.uuid, action.payload.instanceId, action.payload.name, action.payload.zoneUids)
                    .pipe(
                        tap(_ => {
                            if (action.payload.callbackEmitter) {
                                action.payload.callbackEmitter.emit(null);
                            }
                        }),
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid),
                        map(_ => {
                            if (action.payload.logAction) {
                                const payload = action.payload;
                                this.auditLogService.logEvent(payload.logAction.auditName, {
                                    data: payload.logAction.data,
                                    optionalParams: { uuid: payload.uuid },
                                    entityId: payload.logAction.entityId,
                                    projectId: payload.logAction.projectId,
                                });
                            }
                            return new CHServiceActions.UpdateServiceNameAndZoneSuccess({
                                uuid: action.payload.uuid,
                                instanceId: action.payload.instanceId,
                                name: action.payload.name,
                                zoneUids: action.payload.zoneUids,
                            });
                        }),
                        catchError(DataActions.toError(CHServiceActions.UpdateServiceNameAndZoneFailure))
                    )
            )
        )
    );

    updateServiceNameAndZoneSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.UpdateServiceNameAndZoneSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "device"))
        )
    );

    updateServiceNameAndZoneFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(CHServiceActions.ActionTypes.UpdateServiceNameAndZoneFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, "device"))
        )
    );
}
